// Where the API is.
//
// ══════════════════════════════════════════════════════════════════════════════
//  ⚠⚠ THE API BASE IS NEVER `https://quality.majans.com/api`.
//
//  quality.majans.com is the Static Web App. An SWA custom domain does NOT
//  forward /api to a separate Function App — the requests land on the static
//  host, which answers 404 for anything it cannot find. Every GET degrades to an
//  empty screen and every POST silently does nothing, with no error in Azure
//  because nothing ever reached the API. That exact configuration cost a sibling
//  app eight days of writes that appeared to succeed.
//
//  The Function App is its own host: https://quality-api-v1.azurewebsites.net/api
//
//  ACCEPTANCE TEST — CHECK THE CONTENT TYPE, NOT THE STATUS CODE.
//      curl -i <base>/health   →  200 application/json  = the API
//                              →  200 text/html         = WRONG HOST
//      curl -i <base>/runs     →  401                   = reachable, auth working
//
//  ⚠ AN EARLIER VERSION OF THIS COMMENT SAID "404 or 405 = WRONG HOST", AND IT
//  WAS WRONG IN THE DANGEROUS DIRECTION. Measured 2026-09-17, once the custom
//  domain was bound: https://quality.majans.com/api/health answers 200 with
//  text/html, because the SPA's navigation fallback serves index.html for any
//  path it does not recognise. A misconfigured caller reads 200 OK as working.
//  The status code cannot tell the two hosts apart. The content type can.
// ══════════════════════════════════════════════════════════════════════════════

/** The deployed Function App. Not the SWA domain — see above. */
const PRODUCTION_API = 'https://quality-api-v1.azurewebsites.net/api';

/** `func start` in api/. Absolute, not a Vite proxy: what works locally should be
 *  the same mechanism that ships, or local success proves nothing. */
const DEVELOPMENT_API = 'http://localhost:7071/api';

const configured = (import.meta.env.VITE_API_URL || '').trim();

export const API_BASE = (
  configured || (import.meta.env.DEV ? DEVELOPMENT_API : PRODUCTION_API)
).replace(/\/+$/, '');

/**
 * Set when VITE_API_URL points at the static host. App.tsx renders it instead of
 * the app, because every call in this app goes through this one base — a base
 * pointing at the SWA means nothing works, and a hundred silent 404s is a much
 * worse way to learn that than one sentence on the screen.
 *
 * Reported, not thrown: a module-scope throw happens before React exists, so
 * nothing can catch it and the user gets a white page.
 */
export const API_CONFIG_ERROR: string | null = /quality\.majans\.com/i.test(API_BASE)
  ? `VITE_API_URL is set to ${API_BASE}. An SWA custom domain does not forward /api, so every ` +
    `request would 404 against the static host instead of reaching the API. Use ${PRODUCTION_API}.`
  : null;

if (API_CONFIG_ERROR) console.error('[config]', API_CONFIG_ERROR);

/** Join the base with a route path, tolerating a leading slash either way. */
export function apiUrl(path: string): string {
  // Also refused here, not only at the curtain: the offline queue replays without
  // any UI in front of it, and a queue quietly posting readings into a 404 is the
  // exact silent loss this app exists to prevent.
  if (API_CONFIG_ERROR) throw new Error(API_CONFIG_ERROR);
  return `${API_BASE}/${path.replace(/^\/+/, '')}`;
}
